import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardDescription } from '../ui/card';
import { Button } from '../ui/button';

export const number = value => (value == null ? '—' : new Intl.NumberFormat('es-UY').format(value));
export const money = (value, currency = 'UYU') =>
  value == null
    ? '—'
    : new Intl.NumberFormat('es-UY', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value);

export function ReportPanel({ title, description, action, children, id, className = '' }) {
  return (
    <Card id={id} className={`min-w-0 scroll-mt-6 ${className}`}>
      <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-3 space-y-0'>
        <div className='min-w-0 flex-1'>
          <h2 className='m-0 text-lg font-semibold tracking-tight text-foreground'>{title}</h2>
          {description && <CardDescription className='mb-0 mt-1'>{description}</CardDescription>}
        </div>
        {action}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function EmptyReport({ children }) {
  return (
    <p className='mb-0 rounded-lg bg-muted/60 px-4 py-8 text-center text-sm text-muted-foreground'>
      {children}
    </p>
  );
}

export function ReportState({ report, label, children }) {
  if (report.state === 'loading' || report.state === 'idle') {
    return (
      <div role='status' className='space-y-4 py-3'>
        <span className='text-sm text-muted-foreground'>Cargando {label}…</span>
        <div className='h-16 animate-pulse rounded-lg bg-muted motion-reduce:animate-none' />
        <div className='h-3 w-2/3 animate-pulse rounded bg-muted motion-reduce:animate-none' />
      </div>
    );
  }
  if (report.state === 'error') {
    return (
      <div role='alert' className='rounded-lg border border-destructive/20 bg-destructive/5 p-4'>
        <p className='text-sm text-foreground'>No se pudieron cargar {label}.</p>
        <Button type='button' size='sm' variant='outline' onClick={report.retry}>
          <RefreshCw aria-hidden='true' className='h-4 w-4' /> Reintentar {label}
        </Button>
      </div>
    );
  }
  return children;
}

/** Direct labels remain readable on mobile and preserve negative values around a zero baseline. */
export function ReportBars({ rows, valueKey, format = number, color = 'primary', unit = '' }) {
  const minimum = Math.min(0, ...rows.map(row => Number(row[valueKey]) || 0));
  const maximum = Math.max(0, ...rows.map(row => Number(row[valueKey]) || 0));
  const span = maximum - minimum || 1;
  const zero = (-minimum / span) * 100;
  return (
    <ul className='m-0 list-none space-y-5 p-0'>
      {rows.map((row, index) => {
        const value = Number(row[valueKey]) || 0;
        return (
          <li key={row.id || `${row.name}-${index}`}>
            <div className='mb-2 flex items-start justify-between gap-4 text-sm'>
              <span className='min-w-0 break-words text-foreground'>{row.name}</span>
              <span className='shrink-0 font-medium tabular-nums text-foreground'>
                {format(value)}
                {unit && ` ${unit}`}
              </span>
            </div>
            <div className='relative h-2 overflow-hidden rounded-full bg-muted' aria-hidden='true'>
              <div
                className='absolute h-full rounded-full'
                style={{
                  background: `hsl(var(--${color}))`,
                  left: `${value < 0 ? zero - (Math.abs(value) / span) * 100 : zero}%`,
                  width: `${(Math.abs(value) / span) * 100}%`,
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
